import React, { useState, useRef, useEffect } from "react";
import {
  DndContext,
  closestCenter,
  PointerSensor,
  useSensor,
  useSensors
} from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  useSortable,
  verticalListSortingStrategy
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';

function Avatar({ initials }) {
  if (typeof initials === 'object') return initials;
  return <span className="inline-flex items-center justify-center w-7 h-7 rounded-full bg-gray-200 dark:bg-gray-600 font-bold text-sm text-gray-700 dark:text-gray-100">{initials}</span>;
}

const defaultGetSubitems = () => [
  { subitem: 'Add an "All"', owner: 'AZ', colab: 'AZ', status: 'Revise', priority: 'Medium', msgCount: 1 },
  { subitem: 'LinkedIn Image', owner: 'AZ', colab: 'JD', status: 'Revise', priority: 'Medium', msgCount: 1 },
];

function RowDraggable({ id, children, ...props }) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id });
  return (
    <tr
      ref={setNodeRef}
      style={{
        transform: CSS.Transform.toString(transform),
        transition,
        opacity: isDragging ? 0.5 : 1,
      }}
      {...props}
    >
      {React.Children.map(children, (child, idx) => {
        if (idx === 0 && React.isValidElement(child)) {
          // Add drag handle to first cell
          return React.cloneElement(child, {
            children: (
              <>
                <span {...attributes} {...listeners} style={{ cursor: 'grab', marginRight: 8, display: 'inline-block' }} title="Drag to reorder">
                  <svg width="16" height="16" fill="none" viewBox="0 0 24 24"><circle cx="6" cy="7" r="1.5" fill="#888"/><circle cx="6" cy="12" r="1.5" fill="#888"/><circle cx="6" cy="17" r="1.5" fill="#888"/><circle cx="12" cy="7" r="1.5" fill="#888"/><circle cx="12" cy="12" r="1.5" fill="#888"/><circle cx="12" cy="17" r="1.5" fill="#888"/><circle cx="18" cy="7" r="1.5" fill="#888"/><circle cx="18" cy="12" r="1.5" fill="#888"/><circle cx="18" cy="17" r="1.5" fill="#888"/></svg>
                </span>
                {child.props.children}
              </>
            )
          });
        }
        return child;
      })}
    </tr>
  );
}

const tableContainerStyle = {
  overflowX: 'hidden',
  scrollbarWidth: 'none', // Firefox
  msOverflowStyle: 'none', // IE 10+
};

export default function TableSection({
  title,
  borderColor,
  tasks,
  getSubitems = defaultGetSubitems,
  onAddSubitem,
  newSubitemText,
  setNewSubitemText,
  addingSubitemFor,
  setAddingSubitemFor,
  newItemText,
  setNewItemText,
  handleAddItem,
  onReorder,
}) {
  const [expandedRow, setExpandedRow] = useState(null);
  const [hoveredItemIdx, setHoveredItemIdx] = useState(null);
  const [sidebarItem, setSidebarItem] = useState(null);
  const [activeTab, setActiveTab] = useState('updates');
  const [showMenu, setShowMenu] = useState(false);
  const [showEmailDropdown, setShowEmailDropdown] = useState(false);
  const [emailPref, setEmailPref] = useState('Instant email');
  const menuRef = useRef(null);
  const [darkMode, setDarkMode] = useState(false);

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 5 } })
  );

  function handleDragEnd(event) {
    const { active, over } = event;
    if (!over) return;
    const oldIndex = tasks.findIndex(t => t.id ? t.id === active.id : t.item === active.id);
    const newIndex = tasks.findIndex(t => t.id ? t.id === over.id : t.item === over.id);
    if (oldIndex !== -1 && newIndex !== -1 && oldIndex !== newIndex) {
      const newTasks = arrayMove(tasks, oldIndex, newIndex);
      if (onReorder) onReorder(newTasks);
    }
  }

  // Ensure each task has a unique id for dnd-kit
  const dndTasks = tasks.map((t, i) => ({ ...t, dndId: t.id || t.item || String(i) }));

  // Close menu on outside click
  useEffect(() => {
    function handleClickOutside(event) {
      if (menuRef.current && !menuRef.current.contains(event.target)) {
        setShowMenu(false);
        setShowEmailDropdown(false);
      }
    }
    if (showMenu) document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [showMenu]);

  return (
    <div className="mb-8">
      <div className={`flex items-center text-lg font-semibold mb-2 border-l-4 pl-2 ${borderColor}`}>{title}</div>
      <div className="overflow-x-auto rounded-2xl shadow bg-white dark:bg-gray-900" style={tableContainerStyle}>
        <style>{`
          .overflow-x-auto::-webkit-scrollbar { display: none; }
        `}</style>
        <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
          <SortableContext items={dndTasks.map(t => t.dndId)} strategy={verticalListSortingStrategy}>
            <table className="min-w-full">
              <thead>
                <tr className="text-left text-xs text-gray-500">
                  <th className="p-3 w-10"><input type="checkbox" /></th>
                  <th className="p-3">Item</th>
                  <th className="p-3">Developer</th>
                  <th className="p-3">Support</th>
                  <th className="p-3">Requested by</th>
                  <th className="p-3">Status</th>
                  <th className="p-3">Priority</th>
                </tr>
              </thead>
              <tbody>
                {dndTasks.map((task, idx) => (
                  <RowDraggable key={task.dndId} id={task.dndId}>
                    <td className="p-3 w-10 align-middle"><input type="checkbox" /></td>
                    <td
                      className="p-3 align-middle cursor-pointer"
                      onMouseEnter={() => setHoveredItemIdx(idx)}
                      onMouseLeave={() => setHoveredItemIdx(null)}
                    >
                      <div className="flex items-center gap-2">
                        {/* Left chevron for expand */}
                        <span className="flex items-center justify-center w-5 h-5" style={{ minWidth: 20 }}>
                          <span
                            style={{ opacity: hoveredItemIdx === idx ? 1 : 0, pointerEvents: hoveredItemIdx === idx ? 'auto' : 'none', transition: 'opacity 0.15s' }}
                            className="cursor-pointer"
                            onClick={e => { e.stopPropagation(); setExpandedRow(expandedRow === idx ? null : idx); }}
                            title="Expand item"
                          >
                            <svg width="18" height="18" fill="none" viewBox="0 0 24 24"><path d="M9 6l6 6-6 6" stroke="#888" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/></svg>
                          </span>
                        </span>
                        <span>{task.item}</span>
                        {/* Right arrow for sidebar modal */}
                        <span className="flex items-center justify-center w-5 h-5" style={{ minWidth: 20 }}>
                          <span
                            style={{ opacity: hoveredItemIdx === idx ? 1 : 0, pointerEvents: hoveredItemIdx === idx ? 'auto' : 'none', transition: 'opacity 0.15s' }}
                            className="cursor-pointer"
                            onClick={e => { e.stopPropagation(); setSidebarItem(task); }}
                            title="Open item page"
                          >
                            <svg width="18" height="18" fill="none" viewBox="0 0 24 24"><path d="M5 12h14M13 6l6 6-6 6" stroke="#888" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/></svg>
                          </span>
                        </span>
                      </div>
                    </td>
                    <td className="p-3 align-middle"><Avatar initials={task.developer} /></td>
                    <td className="p-3 align-middle"><Avatar initials={task.support} /></td>
                    <td className="p-3 align-middle"><Avatar initials={task.requestedBy} /></td>
                    <td className="p-3 align-middle"><span className={`px-3 py-1 rounded-lg text-white text-xs font-semibold ${task.status.color}`}>{task.status.label}</span></td>
                    <td className="p-3 align-middle"><span className={`px-3 py-1 rounded-lg text-white text-xs font-semibold ${task.priority.color}`}>{task.priority.label}</span></td>
                  </RowDraggable>
                ))}
                {/* Add item row */}
                {handleAddItem && (
                  <tr>
                    <td className="p-3 w-10 align-middle"><input type="checkbox" disabled /></td>
                    <td className="p-3 align-middle text-gray-400">
                      <input
                        type="text"
                        className="w-full bg-transparent outline-none"
                        placeholder="+ Add item"
                        value={newItemText}
                        onChange={e => setNewItemText(e.target.value)}
                        onKeyDown={e => { if (e.key === 'Enter') handleAddItem(); }}
                      />
                    </td>
                    <td colSpan={5}></td>
                  </tr>
                )}
              </tbody>
            </table>
          </SortableContext>
        </DndContext>
      </div>
      {/* Sidebar modal */}
      {sidebarItem && (
        <div className="fixed inset-0 bg-black bg-opacity-30 z-50 flex justify-end">
          <div className="w-[900px] max-w-full h-full bg-white dark:bg-gray-900 shadow-2xl p-0 relative flex flex-col">
            {/* Header */}
            <div className="flex items-center justify-between px-6 py-5 border-b border-gray-200">
              <div className="text-2xl font-bold">Event Boost feature</div>
              <div className="flex items-center gap-2 relative">
                {/* Avatars */}
                <span className="flex -space-x-2 mr-2">
                  <span className="w-8 h-8 rounded-full bg-green-500 text-white flex items-center justify-center font-bold border-2 border-white">AZ</span>
                  <span className="w-8 h-8 rounded-full bg-yellow-500 text-white flex items-center justify-center font-bold border-2 border-white">AC</span>
                  <span className="w-8 h-8 rounded-full bg-blue-500 text-white flex items-center justify-center font-bold border-2 border-white">JD</span>
                </span>
                {/* 3-dots menu button */}
                <div className="relative">
                  <button
                    className="w-10 h-10 flex items-center justify-center rounded-full hover:bg-gray-100 text-gray-500 text-2xl relative"
                    aria-label="More options"
                    type="button"
                    onClick={() => setShowMenu(v => !v)}
                  >
                    <svg width="24" height="24" fill="none" viewBox="0 0 24 24"><circle cx="5" cy="12" r="2" fill="#888"/><circle cx="12" cy="12" r="2" fill="#888"/><circle cx="19" cy="12" r="2" fill="#888"/></svg>
                  </button>
                  {showMenu && (
                    <div ref={menuRef} className="absolute right-0 mt-2 w-64 bg-white rounded-xl shadow-xl border border-gray-100 z-50">
                      <button className="w-full flex items-center gap-2 px-4 py-3 text-gray-800 hover:bg-gray-50 text-sm font-medium rounded-t-xl">
                        <svg width="18" height="18" fill="none" viewBox="0 0 24 24"><path d="M8 12h8M12 8v8" stroke="#888" strokeWidth="2"/></svg>
                        Manage subscribers
                      </button>
                      <div className="relative group">
                        <button
                          className="w-full flex items-center gap-2 px-4 py-3 text-gray-800 hover:bg-gray-50 text-sm font-medium"
                          onMouseEnter={() => setShowEmailDropdown(true)}
                          onMouseLeave={() => setShowEmailDropdown(false)}
                        >
                          <svg width="18" height="18" fill="none" viewBox="0 0 24 24"><rect x="3" y="5" width="18" height="14" rx="2" stroke="#888" strokeWidth="2"/><path d="M3 7l9 6 9-6" stroke="#888" strokeWidth="2"/></svg>
                          Email preferences
                          <svg width="16" height="16" fill="none" viewBox="0 0 24 24" className="ml-auto"><path d="M9 10l3 3 3-3" stroke="#888" strokeWidth="2"/></svg>
                        </button>
                        {showEmailDropdown && (
                          <div
                            className="absolute left-[-180px] top-0 w-44 bg-white rounded-xl shadow-xl border border-gray-100 z-50"
                            onMouseEnter={() => setShowEmailDropdown(true)}
                            onMouseLeave={() => setShowEmailDropdown(false)}
                          >
                            <button
                              className={`w-full flex items-center gap-2 px-4 py-3 text-gray-800 hover:bg-gray-50 text-sm font-medium ${emailPref === 'Instant email' ? 'font-bold' : ''}`}
                              onClick={() => { setEmailPref('Instant email'); setShowMenu(false); }}
                            >
                              {emailPref === 'Instant email' && <svg width="16" height="16" fill="none" viewBox="0 0 24 24"><path d="M5 13l4 4L19 7" stroke="#2563eb" strokeWidth="2"/></svg>}
                              Instant email
                            </button>
                            <button
                              className={`w-full flex items-center gap-2 px-4 py-3 text-gray-800 hover:bg-gray-50 text-sm font-medium ${emailPref === 'No email' ? 'font-bold' : ''}`}
                              onClick={() => { setEmailPref('No email'); setShowMenu(false); }}
                            >
                              {emailPref === 'No email' && <svg width="16" height="16" fill="none" viewBox="0 0 24 24"><path d="M5 13l4 4L19 7" stroke="#2563eb" strokeWidth="2"/></svg>}
                              No email
                            </button>
                          </div>
                        )}
                      </div>
                      <button className="w-full flex items-center gap-2 px-4 py-3 text-gray-800 hover:bg-gray-50 text-sm font-medium">
                        <svg width="18" height="18" fill="none" viewBox="0 0 24 24"><rect x="3" y="5" width="18" height="14" rx="2" stroke="#888" strokeWidth="2"/><path d="M8 11h8M8 15h6" stroke="#888" strokeWidth="2"/></svg>
                        Export updates to Excel
                      </button>
                      <button className="w-full flex items-center gap-2 px-4 py-3 text-gray-800 hover:bg-gray-50 text-sm font-medium">
                        <svg width="18" height="18" fill="none" viewBox="0 0 24 24"><path d="M3 12h18M12 3v18" stroke="#888" strokeWidth="2"/></svg>
                        Copy item link
                      </button>
                      <button className="w-full flex items-center gap-2 px-4 py-3 text-gray-400 text-sm font-medium cursor-not-allowed" disabled>
                        <svg width="18" height="18" fill="none" viewBox="0 0 24 24"><rect x="5" y="11" width="14" height="7" rx="2" stroke="#bbb" strokeWidth="2"/><path d="M9 11V7a3 3 0 116 0v4" stroke="#bbb" strokeWidth="2"/></svg>
                        Archive
                      </button>
                      <button className="w-full flex items-center gap-2 px-4 py-3 text-gray-400 text-sm font-medium cursor-not-allowed rounded-b-xl" disabled>
                        <svg width="18" height="18" fill="none" viewBox="0 0 24 24"><rect x="5" y="5" width="14" height="14" rx="2" stroke="#bbb" strokeWidth="2"/><path d="M9 9l6 6M15 9l-6 6" stroke="#bbb" strokeWidth="2"/></svg>
                        Delete
                      </button>
                    </div>
                  )}
                </div>
                {/* Cross icon to close modal */}
                <button
                  className="w-10 h-10 flex items-center justify-center rounded-full hover:bg-gray-100 text-gray-500 text-2xl ml-1"
                  aria-label="Close sidebar"
                  onClick={() => setSidebarItem(null)}
                >
                  <svg width="28" height="28" fill="none" viewBox="0 0 24 24"><path d="M18 6L6 18M6 6l12 12" stroke="#888" strokeWidth="2" strokeLinecap="round"/></svg>
                </button>
              </div>
            </div>
            {/* Tabs */}
            <div className="flex items-center border-b border-gray-200 px-6">
              <button onClick={() => setActiveTab('updates')} className={`py-3 px-4 text-sm font-semibold focus:outline-none ${activeTab === 'updates' ? 'border-b-2 border-blue-600 text-blue-600' : 'text-gray-500 hover:text-blue-600'}`}>Updates <span className="ml-1 text-xs text-gray-400">/ 5</span></button>
              <button onClick={() => setActiveTab('files')} className={`py-3 px-4 text-sm font-semibold focus:outline-none ${activeTab === 'files' ? 'border-b-2 border-blue-600 text-blue-600' : 'text-gray-500 hover:text-blue-600'}`}>Files</button>
              <button onClick={() => setActiveTab('activity')} className={`py-3 px-4 text-sm font-semibold focus:outline-none ${activeTab === 'activity' ? 'border-b-2 border-blue-600 text-blue-600' : 'text-gray-500 hover:text-blue-600'}`}>Activity Log</button>
              <button className="ml-2 p-1 rounded-full hover:bg-gray-100"><span className="text-xl">+</span></button>
            </div>
            {/* Tab Content */}
            {activeTab === 'updates' && (
              <>
                {/* Update input section */}
                <div className="px-6 py-4 border-b border-gray-200">
                  <div className="flex items-center justify-between mb-2">
                    <input type="text" className="w-full border rounded-lg px-4 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-200" placeholder="Write an update and mention others with @" />
                  </div>
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <button className="flex items-center gap-1 text-gray-500 hover:text-blue-600 text-sm"><svg width="18" height="18" fill="none" viewBox="0 0 24 24"><path d="M16 12a4 4 0 11-8 0 4 4 0 018 0z" stroke="#888" strokeWidth="2"/><path d="M12 2v2m0 16v2m10-10h-2M4 12H2m15.07 7.07l-1.42-1.42M6.34 6.34L4.93 4.93m12.02 0l-1.41 1.41M6.34 17.66l-1.41 1.41" stroke="#888" strokeWidth="2"/></svg> @ Mention</button>
                      <button className="flex items-center gap-1 text-gray-500 hover:text-blue-600 text-sm"><svg width="18" height="18" fill="none" viewBox="0 0 24 24"><path d="M9 17v-2a4 4 0 014-4h4" stroke="#888" strokeWidth="2"/><rect x="3" y="3" width="18" height="18" rx="2" stroke="#888" strokeWidth="2"/></svg> GIF</button>
                      <button className="flex items-center gap-1 text-gray-500 hover:text-blue-600 text-sm"><svg width="18" height="18" fill="none" viewBox="0 0 24 24"><circle cx="12" cy="12" r="10" stroke="#888" strokeWidth="2"/><circle cx="12" cy="10" r="3" stroke="#888" strokeWidth="2"/><path d="M12 13v2" stroke="#888" strokeWidth="2"/></svg></button>
                    </div>
                    <div className="flex items-center gap-4">
                      <button className="text-xs text-gray-500 hover:underline">Update via email</button>
                      <button className="text-xs text-gray-500 hover:underline">Give feedback</button>
                    </div>
                  </div>
                </div>
                {/* Updates list */}
                <div className="px-6 py-4 flex-1 overflow-y-auto">
                  {/* Update card */}
                  <div className="rounded-xl border border-gray-200 bg-white p-4 mb-4">
                    <div className="flex items-center gap-3 mb-1">
                      <img src="https://randomuser.me/api/portraits/men/32.jpg" alt="Andrew" className="w-9 h-9 rounded-full object-cover" />
                      <span className="font-bold text-gray-800">Andrew</span>
                      <span className="text-xs text-gray-400">23d</span>
                      <span className="ml-auto flex gap-2">
                        <span className="text-xl text-gray-300">✨</span>
                        <span className="text-xl text-gray-300">🔔</span>
                        <span className="text-xl text-gray-300">⋯</span>
                      </span>
                    </div>
                    <div className="mb-2"><span className="text-blue-600 font-medium cursor-pointer">@Aleem Zada</span> we can work on this next to finish.</div>
                    <div className="flex items-center gap-4 mt-2">
                      <button className="flex items-center gap-1 text-gray-500 hover:text-blue-600 text-sm"><svg width="18" height="18" fill="none" viewBox="0 0 24 24"><path d="M6 21v-2a4 4 0 014-4h4a4 4 0 014 4v2" stroke="#888" strokeWidth="2"/><circle cx="12" cy="7" r="4" stroke="#888" strokeWidth="2"/></svg> Like</button>
                      <button className="flex items-center gap-1 text-gray-500 hover:text-blue-600 text-sm"><svg width="18" height="18" fill="none" viewBox="0 0 24 24"><path d="M21 15a2 2 0 01-2 2H7l-4 4V5a2 2 0 012-2h12a2 2 0 012 2z" stroke="#888" strokeWidth="2"/></svg> Reply</button>
                      <span className="text-xs text-gray-400 ml-auto flex items-center gap-1"><svg width="16" height="16" fill="none" viewBox="0 0 24 24"><path d="M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6.002 6.002 0 00-4-5.659V4a2 2 0 10-4 0v1.341C7.67 6.165 6 8.388 6 11v3.159c0 .538-.214 1.055-.595 1.436L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9" stroke="#888" strokeWidth="2"/></svg>2</span>
                    </div>
                  </div>
                </div>
              </>
            )}
            {activeTab === 'files' && (
              <div className="flex-1 flex flex-col px-6 py-4">
                <div className="flex items-center gap-2 mb-6">
                  <button className="flex items-center gap-2 px-4 py-2 border border-gray-300 rounded-lg text-gray-700 bg-white hover:bg-gray-50 text-sm font-medium"><span className="text-lg">+</span> Add file</button>
                  <input type="text" className="flex-1 border border-gray-200 rounded-lg px-4 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-200" placeholder="Search for files" />
                  <div className="flex items-center gap-1 ml-auto">
                    <button className="p-2 rounded border border-blue-600 bg-blue-50 text-blue-600"><svg width="18" height="18" fill="none" viewBox="0 0 24 24"><rect x="3" y="3" width="7" height="7" rx="1" stroke="#2563eb" strokeWidth="2"/><rect x="14" y="3" width="7" height="7" rx="1" stroke="#2563eb" strokeWidth="2"/><rect x="14" y="14" width="7" height="7" rx="1" stroke="#2563eb" strokeWidth="2"/><rect x="3" y="14" width="7" height="7" rx="1" stroke="#2563eb" strokeWidth="2"/></svg></button>
                    <button className="p-2 rounded border border-gray-300 text-gray-500 ml-1"><svg width="18" height="18" fill="none" viewBox="0 0 24 24"><rect x="3" y="4" width="18" height="4" rx="1" stroke="#888" strokeWidth="2"/><rect x="3" y="10" width="18" height="4" rx="1" stroke="#888" strokeWidth="2"/><rect x="3" y="16" width="18" height="4" rx="1" stroke="#888" strokeWidth="2"/></svg></button>
                    <button className="p-2 rounded text-gray-500 ml-1"><svg width="18" height="18" fill="none" viewBox="0 0 24 24"><path d="M7 17v2a2 2 0 002 2h6a2 2 0 002-2v-2" stroke="#888" strokeWidth="2"/><path d="M12 12v6m0 0l-3-3m3 3l3-3" stroke="#888" strokeWidth="2"/><rect x="3" y="3" width="18" height="14" rx="2" stroke="#888" strokeWidth="2"/></svg></button>
                  </div>
                </div>
                <div className="flex-1 flex flex-col items-center justify-center">
                  {/* Placeholder illustration */}
                  <svg width="180" height="120" viewBox="0 0 180 120" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <rect x="20" y="70" width="40" height="30" rx="6" fill="#E0F2FE"/>
                    <rect x="70" y="40" width="40" height="40" rx="8" fill="#FEF08A"/>
                    <rect x="120" y="60" width="40" height="30" rx="6" fill="#E0E7FF"/>
                    <polygon points="30,90 40,80 50,90" fill="#34D399"/>
                    <circle cx="90" cy="60" r="10" fill="#6366F1"/>
                    <rect x="110" y="80" width="30" height="10" rx="3" fill="#FBBF24"/>
                    <rect x="60" y="90" width="20" height="10" rx="3" fill="#F87171"/>
                    <rect x="140" y="80" width="10" height="10" rx="2" fill="#A7F3D0"/>
                    <rect x="40" y="100" width="100" height="8" rx="4" fill="#F3F4F6"/>
                    <rect x="80" y="20" width="20" height="10" rx="3" fill="#A5B4FC"/>
                    <rect x="130" y="30" width="10" height="10" rx="2" fill="#FDE68A"/>
                  </svg>
                </div>
              </div>
            )}
            {activeTab === 'activity' && (
              <div className="flex-1 flex flex-col px-6 py-4">
                {/* Section header */}
                <div className="flex items-center justify-between bg-gray-50 rounded-lg px-4 py-3 mb-4">
                  <span className="text-gray-700 font-medium">Other activities</span>
                  <button className="text-blue-600 font-medium hover:underline text-sm">Automation activity</button>
                </div>
                {/* Filter bar */}
                <div className="flex items-center gap-2 mb-4">
                  <button className="bg-blue-600 text-white px-4 py-2 rounded-lg font-medium text-sm flex items-center gap-2"><svg width='18' height='18' fill='none' viewBox='0 0 24 24'><path d='M3 5h18M6 8v11a1 1 0 001 1h10a1 1 0 001-1V8' stroke='#fff' strokeWidth='2' strokeLinecap='round'/></svg>Filter log</button>
                  <button className="flex items-center gap-1 px-3 py-2 rounded-lg border border-gray-200 text-gray-700 text-sm font-medium"><svg width='16' height='16' fill='none' viewBox='0 0 24 24'><circle cx='12' cy='8' r='4' stroke='#888' strokeWidth='2'/><path d='M6 20v-1a4 4 0 014-4h4a4 4 0 014 4v1' stroke='#888' strokeWidth='2'/></svg>Person</button>
                  <div className="flex items-center gap-2 ml-auto">
                    <button className="p-2 rounded hover:bg-gray-100"><svg width='18' height='18' fill='none' viewBox='0 0 24 24'><path d='M4 4v5h.582M20 20v-5h-.581M5 9A7 7 0 0112 5a7 7 0 017 7c0 3.866-3.134 7-7 7a7 7 0 01-7-7zm7 7v3m0 0l-3-3m3 3l3-3' stroke='#888' strokeWidth='2'/></svg></button>
                    <button className="p-2 rounded hover:bg-gray-100"><svg width='18' height='18' fill='none' viewBox='0 0 24 24'><rect x='4' y='4' width='16' height='16' rx='2' stroke='#888' strokeWidth='2'/><path d='M8 8h8M8 12h8M8 16h4' stroke='#888' strokeWidth='2'/></svg></button>
                  </div>
                </div>
                {/* Activity log list */}
                <div className="space-y-4">
                  {/* Row 1 */}
                  <div className="flex items-center gap-3 py-2 border-b">
                    <span className="flex items-center text-gray-400"><svg width='18' height='18' fill='none' viewBox='0 0 24 24'><circle cx='12' cy='12' r='10' stroke='#888' strokeWidth='2'/><path d='M12 6v6l4 2' stroke='#888' strokeWidth='2' strokeLinecap='round'/></svg> <span className="ml-1 text-xs">5d</span></span>
                    <img src="https://randomuser.me/api/portraits/men/32.jpg" alt="User" className="w-7 h-7 rounded-full object-cover" />
                    <span className="font-medium text-gray-800">Event Boost feature</span>
                    <span className="flex items-center gap-1 text-gray-500 ml-4"><svg width='16' height='16' fill='none' viewBox='0 0 24 24'><rect x='4' y='4' width='16' height='16' rx='2' stroke='#888' strokeWidth='2'/><path d='M8 8h8M8 12h8M8 16h4' stroke='#888' strokeWidth='2'/></svg>Subitems</span>
                   
                    <span className="ml-auto flex gap-2">
                    <span className="text-gray-500 ml-4 truncate">Add Social...</span>
                    <span className="text-gray-500 ml-4 truncate">Add an all"</span>
                    </span>
                  </div>
                  {/* Row 2 */}
                  <div className="flex items-center gap-3 py-2 border-b">
                    <span className="flex items-center text-gray-400"><svg width='18' height='18' fill='none' viewBox='0 0 24 24'><circle cx='12' cy='12' r='10' stroke='#888' strokeWidth='2'/><path d='M12 6v6l4 2' stroke='#888' strokeWidth='2' strokeLinecap='round'/></svg> <span className="ml-1 text-xs">22d</span></span>
                    <img src="https://randomuser.me/api/portraits/men/32.jpg" alt="User" className="w-7 h-7 rounded-full object-cover" />
                    <span className="font-medium text-gray-800">Event Boost feature</span>
                    <span className="flex items-center gap-1 text-gray-500 ml-4"><svg width='16' height='16' fill='none' viewBox='0 0 24 24'><rect x='4' y='4' width='16' height='16' rx='2' stroke='#888' strokeWidth='2'/><path d='M8 8h8M8 12h8M8 16h4' stroke='#888' strokeWidth='2'/></svg>Status</span>
                    <span className="ml-4"></span>
                    <span className="ml-4"></span>
                    <span className="ml-auto flex gap-2">
                      <span className="bg-orange-600 text-white px-3 py-1 rounded font-semibold text-xs">Needs Review</span>
                      <span className="bg-blue-600 text-white px-3 py-1 rounded font-semibold text-xs">Pending</span>
                    </span>
                  </div>
                  {/* Row 3 */}
                  <div className="flex items-center gap-3 py-2 border-b">
                    <span className="flex items-center text-gray-400"><svg width='18' height='18' fill='none' viewBox='0 0 24 24'><circle cx='12' cy='12' r='10' stroke='#888' strokeWidth='2'/><path d='M12 6v6l4 2' stroke='#888' strokeWidth='2' strokeLinecap='round'/></svg> <span className="ml-1 text-xs">22d</span></span>
                    <img src="https://randomuser.me/api/portraits/men/32.jpg" alt="User" className="w-7 h-7 rounded-full object-cover" />
                    <span className="font-medium text-gray-800">Event Boost feature</span>
                    <span className="flex items-center gap-1 text-gray-500 ml-4"><svg width='16' height='16' fill='none' viewBox='0 0 24 24'><rect x='4' y='4' width='16' height='16' rx='2' stroke='#888' strokeWidth='2'/><path d='M8 8h8M8 12h8M8 16h4' stroke='#888' strokeWidth='2'/></svg>Status</span>
                    <span className="ml-4"></span>
                    <span className="ml-4"></span>
                    <span className="ml-auto flex gap-2">
                      <span className="bg-yellow-400 text-white px-3 py-1 rounded font-semibold text-xs">Working on...</span>
                      <span className="bg-orange-600 text-white px-3 py-1 rounded font-semibold text-xs">Needs Review</span>
                    </span>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
} 